import { FC, useCallback, useMemo } from 'react';

import {
    GENERATION_SELECTORS_OPTIONS_TRANSLATIONS_MAP,
    GENERATION_SELECTORS_TITLES_TRANSLATIONS_MAP,
    SelectorsEnum,
} from 'utils/constants/aiImageGeneration';

import { createStaticDataProvider, Select, StaticDataFetcherItem, useBreakpoint } from '@hh.ru/magritte-ui';

import useTrls from 'hooks/translation/useTrls';

import { SelectorsValueType, setSelector } from 'store/models/aiImageGeneration';
import { useDispatch, useSelector } from 'store/utils';

import styles from 'components/common/BannerImg/AiImageGenerationModal/GenerationSelectors/GenerationSelectors.module.less';

export const GenerationSelectors: FC = () => {
    const trls = useTrls();
    const dispatch = useDispatch();
    const { isMobile } = useBreakpoint();

    const { selectors, aiImageQueueId } = useSelector((state) => state.aiImageGeneration);

    const dataProviders = useMemo(() => {
        return Object.values(SelectorsEnum).reduce((acc, selector) => {
            acc[selector] = createStaticDataProvider(
                Object.entries(GENERATION_SELECTORS_OPTIONS_TRANSLATIONS_MAP[selector]).map(([key, value]) => ({
                    value: key,
                    text: trls(value),
                }))
            );
            return acc;
        }, {} as Record<SelectorsEnum, ReturnType<typeof createStaticDataProvider>>);
    }, [trls]);

    const getInitialValue = useCallback(
        (selector: SelectorsEnum) => {
            const currentValue = selectors?.[selector] as string;
            if (!currentValue) return undefined;

            const translationMap = GENERATION_SELECTORS_OPTIONS_TRANSLATIONS_MAP[selector];
            const translationKey = translationMap[currentValue as keyof typeof translationMap];

            if (!translationKey) return undefined;

            return {
                value: currentValue,
                text: trls(translationKey),
            };
        },
        [selectors, trls]
    );

    const handleChange = useCallback(
        (selector: SelectorsEnum) => (value: StaticDataFetcherItem) => {
            if (value?.value) {
                dispatch(
                    setSelector({
                        selector,
                        value: value.value as SelectorsValueType,
                    })
                );
            }
        },
        [dispatch]
    );

    const selectorGroups = useMemo(() => {
        const selectorsArray = Object.values(SelectorsEnum);

        if (isMobile) {
            const groups = [];
            for (let i = 0; i < selectorsArray.length; i += 2) {
                groups.push(selectorsArray.slice(i, i + 2));
            }
            return groups;
        }

        return [selectorsArray];
    }, [isMobile]);

    return (
        <div className={styles.container}>
            {selectorGroups.map((group, groupIndex) => (
                <div key={groupIndex} className={styles.row}>
                    {group.map((selector) => (
                        <div className={styles.selector} key={selector}>
                            <Select
                                type="label"
                                value={getInitialValue(selector)}
                                dataProvider={dataProviders[selector]}
                                onChange={handleChange(selector)}
                                triggerProps={{
                                    size: 'large',
                                    label: trls(GENERATION_SELECTORS_TITLES_TRANSLATIONS_MAP[selector]),
                                    disabled: !!aiImageQueueId,
                                    stretched: true,
                                    'data-qa': `generation-selector-${selector}`,
                                    elevateLabel: true,
                                }}
                                bottomSheetHeight="full-screen"
                            />
                        </div>
                    ))}
                </div>
            ))}
        </div>
    );
};
